import { Image } from 'lucide-react'
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import SendReferralDialog from 'src/features/business/referrals/components/send-referral/send-referral-dialog'
import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { UserChatButton } from 'src/features/chats/components/buttons/user-chat-button'
import { sendEvent } from 'src/shared/api/events/send-event'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { Button } from 'src/shared/components/ui/buttons/button'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUserByUsername } from 'src/shared/features/users/api/accounts/use-user-by-username'
import { usePublicLicenses } from 'src/shared/features/users/api/licenses/use-public-licenses'
import CoverPhoto from 'src/shared/features/users/components/profile-photos/cover-photo'
import CoverPhotoUploadDialog from 'src/shared/features/users/components/profile-photos/cover-photo-upload-dialog'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import ProfilePhotoUploadDialog from 'src/shared/features/users/components/profile-photos/profile-photo-upload-dialog'
import { AboutSection } from 'src/shared/features/users/components/view-profile/about-section'
import { BannerSection } from 'src/shared/features/users/components/view-profile/banner-section'
import { PercentSection } from 'src/shared/features/users/components/view-profile/percent-section'
import type { License } from 'src/shared/features/users/schemas/licenses'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import type { PrivateUserData, PublicUserData } from 'src/shared/features/users/schemas/user'
import VisibilityBadge from 'src/shared/features/visibility/components/badge'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useEventStore } from 'src/shared/store/event-store'
import { cn } from 'src/shared/utils/cn'

import { ProfileSectionsContainer } from './public-user-profile'

// ProfileActionButtons component extracted to be reused in footer
const ProfileActionButtons = ({
  user,
  isCurrentUser,
  handleSendReferralClick,
  navigate,
}: {
  user: PublicUserData
  isCurrentUser: boolean
  handleSendReferralClick: () => void
  navigate: (path: string) => void
}) => {
  return (
    <div className="flex w-full items-center justify-between px-4">
      {isCurrentUser ? (
        <VisibilityBadge />
      ) : (
        <div className="min-w-[130px]">
          <UserChatButton userId={user.id} />
        </div>
      )}
      {isCurrentUser ? (
        <Button
          onClick={() => navigate(paths.users.editProfile.getHref())}
          className="min-w-[150px] px-6"
          variant="default"
          size="default"
        >
          Edit Profile
        </Button>
      ) : (
        <SendReferralDialog
          trigger={
            <Button className="min-w-[150px] px-6" variant="default" size="default">
              Send Referral
            </Button>
          }
          receiverUsername={user.username}
          onBeforeOpen={handleSendReferralClick}
        />
      )}
    </div>
  )
}

const AppUserProfile = () => {
  const { username } = useParams<{ username: string }>()
  const { data: currentUser } = useMyUserData()
  const { data: otherUser } = useUserByUsername(username || '')
  const { data: licenses } = usePublicLicenses(otherUser?.id || 0)
  const eventStore = useEventStore()
  const [sessionId, setSessionId] = useState<string | undefined>(eventStore.sessionId || undefined)
  const [flowId, setFlowId] = useState<string | undefined>(eventStore.flowId || undefined)

  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const isCurrentUser = currentUser?.username === username
  const user = isCurrentUser ? currentUser : otherUser

  const [isProfilePhotoDialogOpen, setProfilePhotoDialogOpen] = useState(false)
  const [isCoverPhotoDialogOpen, setCoverPhotoDialogOpen] = useState(false)
  const setReceiver = useSendReferralStore((state) => state.setReceiver)
  const resetSendReferralStore = useSendReferralStore((state) => state.resetSendReferralStore)

  if (!user) {
    navigate('/404')
    return null
  }

  const hasApprovedLicense = licenses?.some((license: License) => license.status === 'approved')
  const isVerified = user.verified && hasApprovedLicense

  const nameParts = user.fullName.split(' ')
  const firstName = nameParts[0]
  const lastName = nameParts.slice(1).join(' ')

  const handleSendReferralClick = () => {
    if (currentUser && currentUser.profileComplete !== 100) {
      return
    }
    resetSendReferralStore()
    setReceiver(user)

    if (!sessionId) {
      setSessionId(eventStore.setSessionId())
    }

    if (!flowId) {
      setFlowId(eventStore.setFlowId())
      sendEvent({
        userId: currentUser?.id,
        eventType: 'flow_start',
        info: {
          sessionId,
          flow_type: 'send_referral',
          flow_step: 'click_send_referral',
          flowId,
          receiver_id: user.id,
        },
      })
    } else {
      sendEvent({
        userId: currentUser?.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flow_type: 'send_referral',
          flow_step: 'click_send_referral',
          flowId,
          receiver_id: user.id,
        },
      })
    }
  }

  return (
    <div>
      <div className="flex min-h-screen flex-col">
        <header className="relative">
          <div className="relative">
            <CoverPhoto userId={user.id} className={`${isMobile ? 'h-24' : 'h-32'} w-full rounded-b-[30px] border-b`} />
            {isCurrentUser && (
              <CoverPhotoUploadDialog
                open={isCoverPhotoDialogOpen}
                onOpenChange={setCoverPhotoDialogOpen}
                trigger={
                  <button
                    type="button"
                    className="absolute bottom-4 right-4 flex size-10 cursor-pointer items-center justify-center rounded-full border border-muted bg-background text-primary hover:bg-accent"
                  >
                    <Image className="size-5" />
                  </button>
                }
              />
            )}
          </div>

          <div className={`absolute ${isMobile ? 'bottom-2 left-4' : 'bottom-2 left-10'} translate-y-1/2`}>
            <div className="relative">
              {isCurrentUser ? (
                <ProfilePhotoUploadDialog
                  open={isProfilePhotoDialogOpen}
                  onOpenChange={setProfilePhotoDialogOpen}
                  trigger={
                    <button type="button">
                      <ProfileAvatar
                        userId={user.id}
                        size={130}
                        className="cursor-pointer border-4 border-background"
                      />
                      <div className="absolute bottom-4 right-0 flex size-10 translate-x-1/4 translate-y-1/4 cursor-pointer items-center justify-center rounded-full border border-muted bg-background text-primary hover:bg-accent">
                        <Image className="size-5" />
                      </div>
                    </button>
                  }
                />
              ) : (
                <ProfileAvatar userId={user.id} size={130} className="border-4 border-background" />
              )}
            </div>
          </div>
        </header>
        <main className="pb-28">
          {' '}
          {/* Increased padding to prevent content from being hidden behind the footer */}
          <section
            className={cn(
              'mt-20 flex',
              isMobile ? 'flex-col justify-start gap-6 px-6' : 'flex-row items-start justify-between px-8 pb-6',
            )}
          >
            <div className="flex flex-col gap-2">
              <div>
                <h1 className="text-3xl font-bold leading-[1.15]">
                  {firstName}{' '}
                  <div className="inline-flex items-center gap-x-1">
                    <p className="truncate">{lastName}</p>
                    <VerificationBadge isVerified={isVerified} className="shrink-0" />
                  </div>
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-2 gap-y-0">
                <p className="text-lg font-semibold">{professionMap[user.profession].label}</p>
                <p className="text-lg text-muted/75">@{user.username}</p>
              </div>
            </div>

            {/* Only show BannerSection here on non-mobile */}
            {!isMobile && (
              <div className="flex items-center">
                <BannerSection user={user} />
              </div>
            )}
          </section>
          {/* Only show BannerSection here on mobile */}
          {isMobile && (
            <div className="my-4 px-6">
              <BannerSection user={user} />
            </div>
          )}
          {isCurrentUser && (
            <div className={cn('mb-6', isMobile ? 'px-6' : 'px-8')}>
              <PercentSection user={user as PrivateUserData} />
            </div>
          )}
          <section className={cn('mb-6', isMobile ? 'px-6' : 'px-8')}>
            <AboutSection user={user} size="default" isCurrentUser={isCurrentUser} />
          </section>
          <div className={cn('mb-32 w-full overflow-clip px-6')}>
            <ProfileSectionsContainer isPublic={false} userId={user.id} />
          </div>
        </main>
      </div>

      {/* Using the FixedFooter component */}
      <FixedFooter
        contentClassName="items-stretch py-2"
        className="z-50 border-t border-border shadow-lg"
        fixedHeight={false}
      >
        <ProfileActionButtons
          user={user}
          isCurrentUser={isCurrentUser}
          handleSendReferralClick={handleSendReferralClick}
          navigate={navigate}
        />
      </FixedFooter>
    </div>
  )
}

export default AppUserProfile
